import { useEffect } from 'react';
import { useAppContext } from '../../Context';
import { AttributesType, ColumnsType, OrderType } from '../../types';

import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/shared/DataTable';

// hooks redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks';
import { setOrder, setOrdersFinished } from '../../slices/ordersSlice';
import { setLoading, setPopConfirmation } from '../../slices/miscSlice';

import { printOrderTable, usePermissions } from '../../services';
import { Grid } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash, faPrint, faBackward } from '@fortawesome/free-solid-svg-icons';
import { constants } from '../../constants';

const ViewOrdersFinished: React.FC = () => {
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ordersFinished = useSelector((state: any) => state.orders.ordersFinished);

  const {
    canView_orders,
    canView_orders_all,
    canView_orders_own,
    canView_menu_orders_finished,
    canDelete_orders,
    canChange_orders,
  } = usePermissions([
    'view_orders',
    'view_orders_all',
    'view_orders_own',
    'view_menu_orders_finished',
    'delete_orders',
    'change_orders',
  ]);

  useEffect(
    () => {
      const getOrders = async () => {
        const response = await apiCall('GET', 'orders/finished/', null, '', 'Error al obtener las ordenes');
        if (response) {
          dispatch(setOrdersFinished(response));
        }
      };

      getOrders();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!(canView_orders || canView_orders_all || canView_orders_own || canView_menu_orders_finished)) {
    navigate('/');
    return null;
  }

  const hoyOnlyDate = new Date();
  hoyOnlyDate.setHours(0, 0, 0, 0);

  const conditionDecorator = {
    operator: '<',
    value: hoyOnlyDate,
    class_decorator: 'text-danger',
    type: 'date',
    limit: constants.settings.limitDateDays,
  };

  const columns: Array<ColumnsType> = [
    { title: 'ID', field: 'id' },
    { title: 'Cliente', field: 'customer_name' },
    { title: 'Línea', field: 'line_name', cell_class: 'text-center' },
    { title: 'Cons', field: 'consecutives_count', cell_class: 'text-center' },
    { title: 'Vendedor', field: 'vendedor', cell_class: 'text-center' },
    { title: 'Fecha', field: 'order_date', cell_class: 'text-center' },
    { title: 'Inicio Prod.', field: 'production_date_start', cell_class: 'text-center' },
    {
      title: 'Fin Prod.',
      field: 'production_date_end',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Entrega',
      field: 'delivery_date',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
  ];

  const handleViewOrder = (order: OrderType) => {
    dispatch(setOrder(order));
    navigate(`/orden/${order.id}`);
  };

  const handleDeleteOrder = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea eliminar la orden?',
        alertType: 'danger',
        okFunc: () => deleteOrder(order.id),
      })
    );
  };
  const deleteOrder = async (order_id: number) => {
    const response = await apiCall('DELETE', `order/${order_id}/`, null, '', 'Error al eliminar la orden');
    if (response) {
      dispatch(setOrdersFinished(ordersFinished.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const handlePrintOrder = async (order: OrderType) => {
    let boceto = null;
    const resp = await apiCall('GET', `order/${order.id}/boceto/`, null, '', 'Error al obtener la imagen');
    if (resp?.image) {
      boceto = resp.image;
    }
    dispatch(setLoading(true));
    await printOrderTable(order, boceto);
    dispatch(setLoading(false));
  };

  const handleAddProcess = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea enviar la orden a producción nuevamente?',
        alertType: 'danger',
        okFunc: () => addProcess(order.id),
      })
    );
  };
  const addProcess = async (order_id: number) => {
    const response = await apiCall(
      'PATCH',
      `order/${order_id}/change_status/${constants.orderStatus.EN_PROCESO}/`,
      null,
      '',
      'Error al devolver la orden a producción'
    );
    if (response) {
      dispatch(setOrdersFinished(ordersFinished.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const canViewOrderDetail =
    canView_orders || canView_orders_all || canView_orders_own || canView_menu_orders_finished;

  const actions: Array<any> = [
    canViewOrderDetail
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: 'Ver',
          onClick: (rowData: OrderType) => handleViewOrder(rowData),
        }
      : null,
    canDelete_orders
      ? {
          icon: <FontAwesomeIcon icon={faTrash} />,
          tooltip: 'Eliminar',
          onClick: (rowData: any) => handleDeleteOrder(rowData),
        }
      : null,
    canView_orders
      ? {
          icon: <FontAwesomeIcon icon={faPrint} />,
          tooltip: 'Imprimir',
          onClick: (rowData: any) => handlePrintOrder(rowData),
        }
      : null,
    canChange_orders
      ? {
          icon: <FontAwesomeIcon icon={faBackward} />,
          tooltip: 'Enviar a Producción nuevamente',
          onClick: (rowData: any) => handleAddProcess(rowData),
        }
      : null,
  ];

  const filterDate: Array<AttributesType> = [
    { value: 'order_date', name: 'Fecha de Creación' },
    { value: 'production_date_start', name: 'Fecha de Inicio de Produccion' },
    { value: 'production_date_end', name: 'Fecha de Fin de Produccion' },
    { value: 'delivery_date', name: 'Fecha de Entrega' },
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>Órdenes Finalizadas</h2>
        </Grid>
      </Grid>
      <DataTable data={ordersFinished} columns={columns} actions={actions} filterByDateBy={filterDate} />
    </div>
  );
};

export default ViewOrdersFinished;
